import React, { useEffect, useState } from "react";
import { X, Send, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const EMPTY = {
  candidate_name: "",
  mobile: "",
  email: "",
  department_id: "",
  inquiry_details: "",
};

export default function AdmissionInquiryModal({ open, onClose, slug, departments = [] }) {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);

  // Reset form whenever the modal is reopened
  useEffect(() => {
    if (open) setForm(EMPTY);
  }, [open]);

  // Close on Escape key
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape" && !submitting) onClose();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose, submitting]);

  if (!open) return null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    const name = form.candidate_name.trim();
    if (!name) {
      toast.error("Please enter your name.");
      return;
    }
    if (name.length > 100) {
      toast.error("Name must be 100 characters or fewer.");
      return;
    }
    const mobile = form.mobile.replace(/\D/g, "");
    if (mobile.length !== 10) {
      toast.error("Mobile must be exactly 10 digits.");
      return;
    }
    if (form.email && !/^\S+@\S+\.\S+$/.test(form.email.trim())) {
      toast.error("Please enter a valid email address.");
      return;
    }
    if (form.inquiry_details.length > 500) {
      toast.error("Inquiry details must be 500 characters or fewer.");
      return;
    }
    try {
      setSubmitting(true);
      await api.post(`/public/admission-inquiry/${slug}`, {
        candidate_name: name,
        mobile,
        email: form.email.trim(),
        department_id: form.department_id || "",
        inquiry_details: form.inquiry_details.trim(),
      });
      toast.success("Inquiry submitted! Our team will reach out shortly.");
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="bul-admission-inquiry-title"
      data-testid="bul-admission-inquiry-modal"
    >
      <div
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
        onClick={() => !submitting && onClose()}
      />
      <div className="relative bg-white shadow-2xl w-full max-w-lg overflow-hidden">
        <div className="bg-[#0F0F10] text-white px-6 py-5 flex items-start justify-between border-b-4 border-[#FDBB30]">
          <div>
            <div className="text-[10px] tracking-[0.35em] uppercase text-[#FDBB30] font-semibold">
              We&apos;d love to hear from you
            </div>
            <h3
              id="bul-admission-inquiry-title"
              className="text-2xl leading-tight mt-1"
              style={{ fontFamily: "'Playfair Display', 'Cormorant Garamond', Georgia, serif", fontWeight: 700 }}
            >
              Contact Our Admission Team
            </h3>
          </div>
          <button
            type="button"
            onClick={() => !submitting && onClose()}
            className="text-white/80 hover:text-white p-1 -m-1"
            data-testid="bul-admission-inquiry-close"
            aria-label="Close inquiry form"
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={submit} className="p-6 space-y-4 max-h-[80vh] overflow-y-auto">
          <Field label="Candidate Name *">
            <input
              type="text"
              maxLength={100}
              value={form.candidate_name}
              onChange={(e) => set("candidate_name", e.target.value)}
              className={inputCls}
              placeholder="Full name"
              data-testid="bul-ai-name"
              autoFocus
            />
            <Hint>
              {form.candidate_name.length}/100 characters
            </Hint>
          </Field>

          <Field label="Mobile Number *">
            <input
              type="tel"
              inputMode="numeric"
              pattern="[0-9]{10}"
              maxLength={10}
              value={form.mobile}
              onChange={(e) => set("mobile", e.target.value.replace(/\D/g, ""))}
              className={inputCls}
              placeholder="10-digit mobile number"
              data-testid="bul-ai-mobile"
            />
            <Hint>10 digits only — no country code, spaces, or dashes.</Hint>
          </Field>

          <Field label="Email ID (Optional)">
            <input
              type="email"
              value={form.email}
              onChange={(e) => set("email", e.target.value)}
              className={inputCls}
              placeholder="you@example.com"
              data-testid="bul-ai-email"
            />
          </Field>

          <Field label="Department Interested In">
            <select
              value={form.department_id}
              onChange={(e) => set("department_id", e.target.value)}
              className={inputCls}
              data-testid="bul-ai-department"
            >
              <option value="">— Select department —</option>
              {departments
                .slice()
                .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                .map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                    {d.code ? ` (${d.code})` : ""}
                  </option>
                ))}
            </select>
          </Field>

          <Field label="Inquiry Details">
            <textarea
              rows={4}
              maxLength={500}
              value={form.inquiry_details}
              onChange={(e) => set("inquiry_details", e.target.value)}
              className={`${inputCls} resize-y min-h-[100px]`}
              placeholder="Tell us a bit about your interest, preferred stream, etc."
              data-testid="bul-ai-details"
            />
            <Hint>{form.inquiry_details.length}/500 characters</Hint>
          </Field>

          <div className="pt-2 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2 text-[11px] uppercase tracking-[0.22em] font-semibold text-[#0F0F10] hover:bg-[#F8F5EE] disabled:opacity-50"
              data-testid="bul-ai-cancel"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center gap-2 bg-[#FDBB30] hover:bg-[#E19E14] text-[#0F0F10] text-[11px] uppercase tracking-[0.22em] font-bold px-6 py-3 disabled:opacity-60 disabled:cursor-not-allowed"
              data-testid="bul-ai-submit"
            >
              {submitting ? (
                <>
                  <Loader2 size={14} className="animate-spin" /> Sending…
                </>
              ) : (
                <>
                  <Send size={14} /> Submit Inquiry
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const inputCls =
  "w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500";

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1">
        {label}
      </div>
      {children}
    </label>
  );
}

function Hint({ children }) {
  return <div className="mt-1 text-[11px] text-slate-500">{children}</div>;
}
